import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import api from "../lib/api";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import { useFlags } from "../lib/flags";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => p?.startsWith("http") ? p : `${BACKEND}${p}`;

export default function Loger() {
    const [lodgings, setLodgings] = useState([]);
    const [filter, setFilter] = useState("all");
    const { flags } = useFlags();
    const showGuides = flags.guides !== false;

    useEffect(() => {
        api.get("/loger").then(r => setLodgings(r.data)).catch(() => {});
    }, []);

    const filters = [
        { id: "all", label: "Tout voir" },
        { id: "hotel", label: "Hôtels" },
        { id: "villa", label: "Villas & gîtes" },
        { id: "event", label: "Lieux d'événement" },
    ];

    const match = (lg, f) => {
        if (f === "all") return true;
        const k = (lg.kind || "").toLowerCase();
        if (f === "hotel") return k.includes("hôtel") || k.includes("resort") || k.includes("boutique");
        if (f === "villa") return k.includes("villa") || k.includes("maison") || k.includes("gîte");
        if (f === "event") return k.includes("événement") || k.includes("château");
        return true;
    };

    const shown = lodgings.filter(l => match(l, filter));

    return (
        <>
            <SEO
            title="Hôtels & maisons d'hôtes chic — Normandie & Picardie"
            description="53 hôtels et maisons d'hôtes vérifiés entre Le Tréport, Cayeux-sur-Mer et Aumale. Hôtel Le Cise (Bois-de-Cise), Domaine de Joinville (Eu), Le Manoir de la Belle Époque (Ault), Le Saint-Yves (Le Tréport)."
            path="/loger"
        />
            <div data-testid="loger-page" className="bg-cp-ivoire">
            <section className="relative min-h-[65vh] bg-cp-encre text-cp-ivoire overflow-hidden cp-grain">
                <img src={`${BACKEND}/api/static/img/mon_loger_villa.jpg`} alt=""
                     className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-cinematic"/>
                <div className="absolute inset-0 cp-hero-overlay"/>
                <div className="relative z-10 min-h-[65vh] max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12 pt-32 md:pt-40 pb-20 flex flex-col justify-end">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
                        <p className="cp-tiny text-cp-sable mb-8">Loger · Notre sélection</p>
                        <h1 className="cp-serif text-[44px] sm:text-[64px] md:text-[88px] lg:text-[112px] xl:text-[140px] leading-[0.94] tracking-[-0.025em] max-w-5xl">
                            Dormir,<br />
                            <em className="cp-display-italic text-cp-sable">autrement.</em>
                        </h1>
                        <p className="cp-serif italic text-base md:text-2xl font-light text-cp-ivoire/90 leading-[1.5] mt-8 max-w-3xl">
                            Huit lieux choisis à la main. Chacun testé, chacun approuvé par la rédaction. Réservation auprès de nos partenaires — Booking, Airbnb, Mr & Mrs Smith — ou en direct pour les plus intimes.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="py-16 md:py-20 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12">
                <div className="flex flex-wrap gap-3 md:gap-4 mb-14" data-testid="loger-filters">
                    {filters.map(f => (
                        <button key={f.id} onClick={() => setFilter(f.id)}
                            data-testid={`loger-filter-${f.id}`}
                            className={`cp-tiny px-5 py-2 border transition-all ${filter === f.id ? "bg-cp-foret text-cp-ivoire border-cp-foret" : "bg-cp-ivoire text-cp-gris border-cp-border hover:border-cp-foret"}`}>
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
                    {shown.map((lg, i) => (
                        <motion.article key={lg.slug}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                            data-testid={`loger-card-${lg.slug}`}
                            className="group">
                            <div className="cp-image-zoom aspect-[4/3] bg-cp-craie overflow-hidden mb-6 relative">
                                {lg.image && (lg.image.includes('/static/img/v3/') || lg.image.includes('/static/img/real/')) ? (
                                    <img src={img(lg.image)} alt={lg.name} loading="lazy"
                                         className="w-full h-full object-cover cp-photo-real"/>
                                ) : (
                                    <PhotoPlaceholder name={lg.name} category={lg.kind?.includes('Villa') ? 'villa' : lg.kind?.includes('Château') ? 'villa' : lg.kind?.includes('Maison') ? 'maison-hotes' : 'hotel'} size="large" />
                                )}
                            </div>
                            <div className="flex items-baseline justify-between gap-4 mb-2">
                                <span className="cp-tiny text-cp-terre">{lg.city}</span>
                                <span className="cp-serif italic text-xl text-cp-foret">dès {lg.price_from}€</span>
                            </div>
                            <h2 className="cp-serif text-3xl md:text-4xl leading-[1] text-cp-encre mb-2 tracking-[-0.01em]">
                                <em className="cp-display-italic">{lg.name}</em>
                            </h2>
                            <p className="cp-tiny text-cp-gris mb-3">{lg.kind}</p>
                            <p className="cp-serif italic text-base text-cp-gris mb-5 leading-snug max-w-xl">{lg.pitch}</p>
                            <div className="flex flex-wrap gap-3">
                                {lg.partners.map((p, j) => (
                                    <a key={j} href={p.url} target="_blank" rel="noopener noreferrer sponsored"
                                       data-testid={`loger-partner-${lg.slug}-${j}`}
                                       className="cp-tiny px-4 py-2 border border-cp-border hover:border-cp-foret hover:text-cp-foret transition-colors">
                                        {p.label} ↗
                                    </a>
                                ))}
                            </div>
                        </motion.article>
                    ))}
                </div>

            </section>

            {showGuides && (
                <section className="bg-cp-craie py-20 md:py-28 border-t border-cp-border">
                    <div className="max-w-[1000px] mx-auto px-5 sm:px-6 md:px-12 text-center">
                        <p className="cp-tiny text-cp-terre mb-6">Vous voulez l'adresse qui n'y est pas ?</p>
                        <h3 className="cp-serif text-4xl md:text-6xl leading-[0.95] text-cp-encre mb-8">
                            Le <em className="cp-display-italic text-cp-foret">Guide des villas privées</em>
                        </h3>
                        <p className="cp-serif italic text-xl text-cp-gris mb-10">Trente maisons à louer — aucune sur les plateformes grand public. Contacts propriétaires directs. 29€.</p>
                        <Link to="/guides/villas-privees" className="cp-btn-primary">Voir le guide →</Link>
                    </div>
                </section>
            )}
        </div>
    </>

    );
}
